import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type HTMLAttributes,
  type ReactNode,
} from 'react'
import { AnimatePresence, motion } from 'framer-motion'

import { useEscapeKey, useOnClickOutside } from '@/hooks'
import { cn } from '@/lib/utils'

interface DropdownMenuContextValue {
  open: boolean
  setOpen: (open: boolean) => void
}

const DropdownMenuContext = createContext<DropdownMenuContextValue | null>(null)

function useDropdownMenuContext(): DropdownMenuContextValue {
  const context = useContext(DropdownMenuContext)
  if (!context) {
    throw new Error('DropdownMenu components must be used within <DropdownMenu>')
  }
  return context
}

export interface DropdownMenuProps {
  children: ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

function DropdownMenu({ children, open: controlledOpen, onOpenChange }: DropdownMenuProps) {
  const [internalOpen, setInternalOpen] = useState(false)
  const open = controlledOpen ?? internalOpen

  const setOpen = useCallback(
    (next: boolean) => {
      setInternalOpen(next)
      onOpenChange?.(next)
    },
    [onOpenChange],
  )

  return (
    <DropdownMenuContext.Provider value={{ open, setOpen }}>
      <div className="relative inline-flex">{children}</div>
    </DropdownMenuContext.Provider>
  )
}

export interface DropdownMenuTriggerProps {
  children?: ReactNode
  className?: string
}

function DropdownMenuTrigger({ children, className }: DropdownMenuTriggerProps) {
  const { open, setOpen } = useDropdownMenuContext()

  return (
    <button
      type="button"
      aria-haspopup="menu"
      aria-expanded={open}
      onClick={() => setOpen(!open)}
      className={cn('inline-flex items-center', className)}
    >
      {children}
    </button>
  )
}

export interface DropdownMenuContentProps extends HTMLAttributes<HTMLDivElement> {
  align?: 'start' | 'end'
}

function DropdownMenuContent({
  children,
  className,
  align = 'end',
}: DropdownMenuContentProps) {
  const { open, setOpen } = useDropdownMenuContext()
  const contentRef = useRef<HTMLDivElement | null>(null)

  const handleClose = useCallback(() => setOpen(false), [setOpen])

  useEscapeKey(handleClose, open)
  useOnClickOutside(handleClose, open)

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          ref={contentRef}
          role="menu"
          initial={{ opacity: 0, y: 6, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 6, scale: 0.98 }}
          transition={{ duration: 0.14, ease: 'easeOut' }}
          className={cn(
            'absolute z-50 mt-2 min-w-44 rounded-xl border border-border bg-popover p-1.5 shadow-soft',
            align === 'end' ? 'right-0' : 'left-0',
            className,
          )}
        >
          {children}
        </motion.div>
      ) : null}
    </AnimatePresence>
  )
}

export interface DropdownMenuItemProps extends HTMLAttributes<HTMLButtonElement> {
  icon?: ReactNode
  onSelect?: () => void
}

function DropdownMenuItem({
  children,
  className,
  icon,
  onSelect,
  ...props
}: DropdownMenuItemProps) {
  const { setOpen } = useDropdownMenuContext()

  return (
    <button
      type="button"
      role="menuitem"
      onClick={() => {
        onSelect?.()
        setOpen(false)
      }}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-foreground outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent disabled:pointer-events-none disabled:opacity-50',
        className,
      )}
      {...props}
    >
      {icon ? (
        <span aria-hidden className="text-muted-foreground [&>svg]:size-4">{icon}</span>
      ) : null}
      <span className="truncate">{children}</span>
    </button>
  )
}

function DropdownMenuLabel({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('px-3 py-2 text-sm font-semibold text-foreground', className)}
      {...props}
    />
  )
}

function DropdownMenuSeparator({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('my-1 h-px bg-border', className)} {...props} />
}

export {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
}